import { Button, ButtonProps } from '@optools/components/ui/button';
import { ButtonGroup, ButtonGroupSeparator } from '@optools/components/ui/button-group';
import { Popover, PopoverContent, PopoverTrigger } from '@optools/components/ui/popover';
import { cn } from '@optools/components/ui/utils';
import { Info } from 'lucide-react';
import { createContext, ReactNode, use } from 'react';

type ButtonSize = Extract<ButtonProps['size'], 'sm' | 'default' | 'lg'>;
type IconSize = Extract<ButtonProps['size'], 'icon' | 'icon-sm' | 'icon-lg'>;

const iconSizeMap: Record<ButtonSize, IconSize> = {
  default: 'icon',
  lg: 'icon-lg',
  sm: 'icon-sm',
};

type InfoButtonContextValue = { size: ButtonSize; variant: ButtonProps['variant'] };
const InfoButtonContext = createContext<InfoButtonContextValue>({} as InfoButtonContextValue);

export interface InfoButtonProps {
  children: ReactNode;
  className?: string;
  description: ReactNode;
  descriptionClassName?: string;
  size?: Extract<ButtonProps['size'], 'sm' | 'default' | 'lg'>;
  variant?: ButtonProps['variant'];
}

export function InfoButton({
  children,
  className,
  description,
  descriptionClassName,
  size = 'default',
  variant = 'outline',
}: InfoButtonProps) {
  const iconSize = iconSizeMap[size];

  return (
    <InfoButtonContext.Provider value={{ size, variant }}>
      <ButtonGroup className={className}>
        {children}
        {variant !== 'outline' && <ButtonGroupSeparator />}
        <Popover>
          <PopoverTrigger asChild>
            <Button aria-label="Open Popover" size={iconSize} variant={variant}>
              <Info />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className={cn('text-xs', descriptionClassName)} side="top">
            {description}
          </PopoverContent>
        </Popover>
      </ButtonGroup>
    </InfoButtonContext.Provider>
  );
}

export function InfoButtonTrigger(props?: React.ComponentProps<typeof Button>) {
  const { size, variant } = use(InfoButtonContext);
  return <Button size={size} variant={variant} {...props} />;
}
